import _ from 'lodash';

import createModel, { prop } from 'models/lib/create_model';
import TaskComment from './task_comment';
import TaskEmail from './task_email';
import TaskFollower from './task_follower';

const TaskInfo = createModel({
  modelName: 'TaskInfo',

  properties: {
    id: prop(String).isRequired,
    comments: prop({}).default({}),
    emails: prop({}).default({}),
    threadChannel: String,
    threadCounterpart: Object,
    followers: prop({}).default({}),
    areCommentsLoading: prop(Boolean),
    areEmailsLoading: prop(Boolean),
    areFollowersLoading: prop(Boolean),
  },

  addComment(comment) {
    this.comments[comment.id] = comment;
  },

  addEmail(email) {
    this.emails[email.id] = email;
  },

  setThread({ channel, counterpart }) {
    this.threadChannel = channel;
    this.threadCounterpart = counterpart;
  },

  addFollower(follower) {
    this.followers[follower.id] = follower;
  },

  getComments() {
    return _.orderBy(this.comments, ['createdAt']);
  },

  getEmails() {
    return _.orderBy(this.emails, [email => Date.parse(email.timestamp)]);
  },

  // What a reply into the thread starts from: the address we wrote from (or were written to), the
  // counterpart, everyone else on the latest message as cc, and the latest subject. "Re:" stays
  // English on purpose: it is the wire convention mail clients thread on, not UI copy, so a subject
  // already carrying any common reply prefix is left alone rather than stacked.
  replyDefaults() {
    const emails = this.getEmails();
    const firstOutbound = emails.find(email => email.isOutbound());
    const firstInbound = emails.find(email => email.isInbound());
    const from = (firstOutbound && firstOutbound.from) || (firstInbound && (firstInbound.to || [])[0]) || '';
    const to = this.threadCounterpart ? [this.threadCounterpart.address] : [];
    const latest = emails[emails.length - 1] || {};
    const excluded = [from, ...to].map(normalizeAddress);
    const cc = _.uniqBy(
      [latest.from, ...(latest.to || []), ...(latest.cc || [])].filter(
        address => address && !excluded.includes(normalizeAddress(address))
      ),
      normalizeAddress
    );
    const latestSubject = latest.subject || '';
    const subject = !latestSubject || REPLY_PREFIX.test(latestSubject) ? latestSubject : `Re: ${latestSubject}`;
    return { from, to, cc, subject };
  },

  getThread() {
    const entries = [
      ..._.map(this.comments, comment => ({ id: comment.id, kind: 'comment', at: comment.createdAt, comment })),
      ..._.map(this.emails, email => ({ id: email.id, kind: 'email', at: email.timestamp, email })),
    ];
    // Sort on the parsed instant: comments and emails come from different endpoints, so their
    // ISO-8601 strings need not share precision or offset form, and comparing them as text misorders
    // (".500Z" sorts before "Z").
    return _.orderBy(entries, [entry => Date.parse(entry.at)]);
  },

  getFollowers() {
    return _.values(this.followers);
  },

  removeFollower({ agentId, itemId }) {
    let removed = _.find(this.followers, f => f.agentId === agentId && f.itemId === itemId);
    if (!removed) return null;
    delete this.followers[removed.id];
    return removed;
  },

  setCommentsLoading() {
    this.areCommentsLoading = true;
  },

  setEmailsLoading() {
    this.areEmailsLoading = true;
  },

  setFollowersLoading() {
    this.areFollowersLoading = true;
  },

  resetCommentsLoading() {
    this.areCommentsLoading = false;
  },

  resetEmailsLoading() {
    this.areEmailsLoading = false;
  },

  resetFollowersLoading() {
    this.areFollowersLoading = false;
  },

  statics: {
    create(attrs) {
      return new this(attrs);
    },

    overrideFromJs(fromJs) {
      return attrs => {
        let comments = {};
        _.forEach(attrs.comments, c => {
          comments[c.id] = TaskComment.fromJs(c);
        });
        let emails = {};
        _.forEach(attrs.emails, e => {
          emails[e.id] = TaskEmail.fromJs(e);
        });
        let followers = {};
        _.forEach(attrs.followers, f => {
          followers[f.id] = new TaskFollower(f);
        });
        return fromJs({ ...attrs, comments, emails, followers });
      };
    },
  },
});

export default TaskInfo;

const REPLY_PREFIX = /^(re|aw|antw|sv|ref)\s*:/i;

function normalizeAddress(address) {
  return address.trim().toLowerCase();
}
